/**
 * AdvancedMarker Component
 *
 * Wrapper for google.maps.marker.AdvancedMarkerElement
 * Replaces the deprecated google.maps.Marker
 *
 * This component uses AdvancedMarkerElement which is the recommended
 * approach as of February 21, 2024.
 */

'use client';

import React, { useEffect, useRef, useState } from 'react';

export interface AdvancedMarkerProps {
  position: { lat: number; lng: number };
  map?: google.maps.Map | null;
  title?: string;
  onClick?: () => void;
  content?: HTMLElement | Node;
  zIndex?: number;
  gmpDraggable?: boolean;
  gmpClickable?: boolean;
}

/**
 * AdvancedMarker Component
 *
 * Creates and manages a google.maps.marker.AdvancedMarkerElement instance
 */
export const AdvancedMarker: React.FC<AdvancedMarkerProps> = ({
  position,
  map,
  title,
  onClick,
  content,
  zIndex,
  gmpDraggable = false,
  gmpClickable = true,
}) => {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const [markerLibrary, setMarkerLibrary] =
    useState<google.maps.MarkerLibrary | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Load the marker library
  useEffect(() => {
    if (typeof window === 'undefined' || !window.google?.maps) {
      return;
    }

    // Check if marker library is already loaded
    if (window.google.maps.marker?.AdvancedMarkerElement) {
      // Library already available, create a mock library object
      setMarkerLibrary({
        AdvancedMarkerElement: window.google.maps.marker.AdvancedMarkerElement,
      } as google.maps.MarkerLibrary);
      return;
    }

    const loadMarkerLibrary = async () => {
      try {
        const library = (await window.google.maps.importLibrary(
          'marker'
        )) as google.maps.MarkerLibrary;
        setMarkerLibrary(library);
      } catch (error) {
        console.error('Error loading marker library:', error);
      }
    };

    loadMarkerLibrary();
  }, []);

  // Create or update the marker
  useEffect(() => {
    if (!map || !markerLibrary || !markerLibrary.AdvancedMarkerElement) {
      return;
    }

    const AdvancedMarkerElement = markerLibrary.AdvancedMarkerElement;

    // Create marker if it doesn't exist
    if (!markerRef.current) {
      const markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
        map,
        position: { lat: position.lat, lng: position.lng },
        title,
        zIndex,
        gmpDraggable,
        gmpClickable,
      };

      // Set content if provided
      // AdvancedMarkerElement content must be a Node (HTMLElement), not a string
      if (content && typeof content !== 'string') {
        markerOptions.content = content;
      } else if (contentRef.current) {
        markerOptions.content = contentRef.current;
      } else if (typeof content === 'string') {
        // If content is a string, create a text node
        const textNode = document.createTextNode(content);
        markerOptions.content = textNode;
      }

      markerRef.current = new AdvancedMarkerElement(markerOptions);

      // Add click listener
      if (onClick) {
        markerRef.current.addListener('click', onClick);
      }
    } else {
      // Update existing marker position
      markerRef.current.position = { lat: position.lat, lng: position.lng };
      if (title !== undefined) {
        markerRef.current.title = title;
      }
      if (zIndex !== undefined) {
        markerRef.current.zIndex = zIndex;
      }
      // Update content if it changed
      // AdvancedMarkerElement content must be a Node (HTMLElement), not a string
      if (content && typeof content !== 'string') {
        markerRef.current.content = content;
      } else if (contentRef.current) {
        markerRef.current.content = contentRef.current;
      } else if (typeof content === 'string') {
        // If content is a string, create a text node
        const textNode = document.createTextNode(content);
        markerRef.current.content = textNode;
      }
    }

    // Cleanup
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, [
    map,
    markerLibrary,
    position,
    title,
    zIndex,
    gmpDraggable,
    gmpClickable,
    content,
    onClick,
  ]);

  // Render content container if no content prop is provided
  // This allows for custom React content in the marker
  if (!content) {
    return (
      <div ref={contentRef} style={{ display: 'none' }} data-marker-content />
    );
  }

  return null;
};
